import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addAssistantChat,
  decideAssistantDmUser,
  getAssistantConfig,
  getAssistantSnapshotProgress,
  refreshAssistantSnapshot,
  registerAssistantWebhook,
  removeAssistantChat,
  removeAssistantDmUser,
  testAssistantConfig,
  updateAssistantChat,
  updateAssistantConfig,
  type AssistantChat,
  type AssistantConfigInput,
  type AssistantDmUser,
  type AssistantTriggerMode,
} from '@/api/assistant';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';

interface FormState {
  enabled: boolean;
  triggerMode: AssistantTriggerMode;
  openaiModel: string;
  githubRepo: string;
  githubRef: string;
  botToken: string;
  openaiKey: string;
  githubToken: string;
  d1ReadToken: string;
  d1AdminToken: string;
}

const EMPTY_FORM: FormState = {
  enabled: false,
  triggerMode: 'mentions',
  openaiModel: 'gpt-5.6-luna',
  githubRepo: '',
  githubRef: 'main',
  botToken: '',
  openaiKey: '',
  githubToken: '',
  d1ReadToken: '',
  d1AdminToken: '',
};

export function AssistantSettings() {
  const queryClient = useQueryClient();
  const { data: config, isLoading } = useQuery({
    queryKey: ['assistant-config'],
    queryFn: getAssistantConfig,
  });
  const { data: snapshotProgress, isError: snapshotPollFailed } = useQuery({
    queryKey: ['assistant-snapshot-progress'],
    queryFn: getAssistantSnapshotProgress,
    refetchInterval: 5_000,
  });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (!config) return;
    setForm({
      enabled: config.enabled,
      triggerMode: config.trigger_mode,
      openaiModel: config.openai_model,
      githubRepo: config.github_repo,
      githubRef: config.github_ref,
      botToken: '',
      openaiKey: '',
      githubToken: '',
      d1ReadToken: '',
      d1AdminToken: '',
    });
  }, [config]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['assistant-config'] });
  const save = useMutation({
    mutationFn: updateAssistantConfig,
    onSuccess: () => {
      setMessage({ ok: true, text: 'Настройки сохранены.' });
      setForm((current) => ({
        ...current,
        botToken: '',
        openaiKey: '',
        githubToken: '',
        d1ReadToken: '',
        d1AdminToken: '',
      }));
      refresh();
    },
    onError: (error: Error) => setMessage({ ok: false, text: error.message }),
  });
  const register = useMutation({
    mutationFn: registerAssistantWebhook,
    onSuccess: (result) => {
      setMessage({
        ok: true,
        text: `Webhook подключён${result.bot_username ? ` для @${result.bot_username}` : ''}.${result.warning ? ` ${result.warning}` : ''}`,
      });
      refresh();
    },
    onError: (error: Error) => setMessage({ ok: false, text: error.message }),
  });
  const test = useMutation({
    mutationFn: testAssistantConfig,
    onSuccess: (result) => setMessage({
      ok: !result.last_error && result.chats.every((chat) => !chat.error),
      text: `@${result.bot_username ?? 'bot'} → ${result.chats.map((chat) => `${chat.title ?? chat.chat_id} (${chat.error ? `ошибка: ${chat.error}` : chat.type})`).join(', ') || 'нет чатов'}; webhook: ${result.webhook_url || 'не подключён'}; pending: ${result.pending_updates}; snapshot: ${result.snapshot_uploaded_at ?? 'нет'} (${result.snapshot_status})${result.snapshot_error ? `; snapshot error: ${result.snapshot_error}` : ''}${result.last_error ? `; webhook error: ${result.last_error}` : ''}`,
    }),
    onError: (error: Error) => setMessage({ ok: false, text: error.message }),
  });
  const refreshSnapshot = useMutation({
    mutationFn: refreshAssistantSnapshot,
    onSuccess: () => {
      setMessage({
        ok: true,
        text: 'Запрошен свежий backup. Прогресс отображается ниже.',
      });
      queryClient.invalidateQueries({ queryKey: ['assistant-snapshot-progress'] });
    },
    onError: (error: Error) => setMessage({ ok: false, text: error.message }),
  });
  const backupActive = snapshotProgress?.backup?.state === 'queued'
    || snapshotProgress?.backup?.state === 'running';
  const snapshotActive = snapshotProgress?.snapshot.status === 'pending'
    || snapshotProgress?.snapshot.status === 'importing';

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const payload: AssistantConfigInput = {
      enabled: form.enabled,
      trigger_mode: form.triggerMode,
      openai_model: form.openaiModel,
      github_repo: form.githubRepo,
      github_ref: form.githubRef,
    };
    if (form.botToken.trim()) payload.bot_token = form.botToken;
    if (form.openaiKey.trim()) payload.openai_api_key = form.openaiKey;
    if (form.githubToken.trim()) payload.github_token = form.githubToken;
    if (form.d1ReadToken.trim()) payload.d1_read_token = form.d1ReadToken;
    if (form.d1AdminToken.trim()) payload.d1_admin_token = form.d1AdminToken;
    setMessage(null);
    save.mutate(payload);
  };

  const removeSecret = (
    field: 'bot_token' | 'openai_api_key' | 'github_token' | 'd1_read_token' | 'd1_admin_token',
    label: string,
  ) => {
    if (!confirm(`Удалить ${label}?`)) return;
    setMessage(null);
    save.mutate({ [field]: '' });
  };

  const inputClass = 'block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm';

  return (
    <section className="bg-surface border border-border rounded-lg p-4 mb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="text-sm font-semibold text-content">AI-ассистент в Telegram</h2>
          <p className="text-xs text-content-subtle mt-1 max-w-3xl">
            Отвечает внутри подключённых групповых чатов (во всех их ветках) и в личке — но только одобренным ниже людям. Каждый ответ отправляется reply на исходное сообщение. GitHub доступен только на чтение.
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
            className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
          />
          <span className="text-sm text-content-muted">Ассистент включён</span>
        </label>
      </div>

      {isLoading ? <p className="text-sm text-content-subtle">Загрузка…</p> : (
        <form onSubmit={submit} className="space-y-4">
          <ChatList
            chats={config?.chats ?? []}
            inputClass={inputClass}
            onMessage={setMessage}
            onChanged={refresh}
          />
          <DmUserList
            users={config?.dm_users ?? []}
            onMessage={setMessage}
            onChanged={refresh}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-content-muted mb-1">Когда отвечать</label>
              <Select
                value={form.triggerMode}
                onChange={(value) => setForm((current) => ({ ...current, triggerMode: value as AssistantTriggerMode }))}
                options={[
                  { value: 'mentions', label: 'Только /ask, @mention или reply боту' },
                  { value: 'all', label: 'На каждое текстовое сообщение' },
                ]}
              />
              <p className="mt-1 text-xs text-content-subtle">Режим общий для всех чатов. Для «все сообщения» надо отключить Group Privacy у @BotFather.</p>
            </div>
            <div />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-content-muted mb-1">OpenAI model</label>
              <input
                value={form.openaiModel}
                onChange={(event) => setForm((current) => ({ ...current, openaiModel: event.target.value }))}
                className={`${inputClass} font-mono`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-content-muted mb-1">GitHub repository / ref</label>
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(100px,0.45fr)] gap-2">
                <input
                  value={form.githubRepo}
                  onChange={(event) => setForm((current) => ({ ...current, githubRepo: event.target.value }))}
                  className={`${inputClass} font-mono`}
                  placeholder="owner/private-repo"
                />
                <input
                  value={form.githubRef}
                  onChange={(event) => setForm((current) => ({ ...current, githubRef: event.target.value }))}
                  className={`${inputClass} font-mono`}
                  placeholder="main"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 border-t border-border pt-4">
            <SecretField
              label="Telegram bot token"
              value={form.botToken}
              onChange={(value) => setForm((current) => ({ ...current, botToken: value }))}
              configured={config?.has_bot_token ?? false}
              preview={config?.bot_token_preview}
              onRemove={() => removeSecret('bot_token', 'Telegram token')}
            />
            <SecretField
              label="OpenAI API key (необязательно)"
              value={form.openaiKey}
              onChange={(value) => setForm((current) => ({ ...current, openaiKey: value }))}
              configured={config?.has_openai_api_key ?? false}
              preview={config?.openai_api_key_preview}
              onRemove={config?.openai_api_key_source === 'assistant'
                ? () => removeSecret('openai_api_key', 'отдельный OpenAI key ассистента')
                : undefined}
            />
            <SecretField
              label="GitHub read-only token"
              value={form.githubToken}
              onChange={(value) => setForm((current) => ({ ...current, githubToken: value }))}
              configured={config?.has_github_token ?? false}
              preview={config?.github_token_preview}
              onRemove={() => removeSecret('github_token', 'GitHub token')}
            />
            <SecretField
              label="Cloudflare D1 Read token"
              value={form.d1ReadToken}
              onChange={(value) => setForm((current) => ({ ...current, d1ReadToken: value }))}
              configured={config?.has_d1_read_token ?? false}
              preview={config?.d1_read_token_preview}
              onRemove={() => removeSecret('d1_read_token', 'D1 Read token')}
            />
            <SecretField
              label="Cloudflare D1 Write token"
              value={form.d1AdminToken}
              onChange={(value) => setForm((current) => ({ ...current, d1AdminToken: value }))}
              configured={config?.has_d1_admin_token ?? false}
              preview={config?.d1_admin_token_preview}
              onRemove={() => removeSecret('d1_admin_token', 'D1 Write token')}
            />
          </div>

          <p className="text-xs text-content-subtle">
            D1 Read используется только для запросов к текущей одноразовой копии. D1 Write — только cron-задачей для создания новой копии и удаления предыдущей. Production DB не передаётся AI.
          </p>

          {message && (
            <div className={`text-xs rounded-md px-3 py-2 ${message.ok ? 'bg-success/10 text-success' : 'bg-danger-soft text-danger'}`}>
              {message.text}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <Button type="submit" loading={save.isPending}>Сохранить</Button>
            <Button type="button" variant="secondary" loading={register.isPending} onClick={() => register.mutate()}>
              Подключить webhook
            </Button>
            <Button type="button" variant="secondary" loading={test.isPending} onClick={() => test.mutate()}>
              Проверить подключение
            </Button>
            <Button
              type="button"
              variant="secondary"
              loading={refreshSnapshot.isPending}
              disabled={backupActive || snapshotActive}
              onClick={() => refreshSnapshot.mutate()}
            >
              Обновить DB snapshot
            </Button>
            <span className="text-xs text-content-subtle">
              {config?.webhook_ready ? `Webhook готов${config.bot_username ? ` · @${config.bot_username}` : ''}` : 'Webhook ещё не настроен'}
              {config?.snapshot_uploaded_at ? ` · snapshot ${config.snapshot_uploaded_at} (${config.snapshot_status})` : ' · snapshot ещё не создан'}
            </span>
          </div>
          {snapshotProgress?.backup && (
            <SnapshotProgress status={snapshotProgress.backup} snapshot={snapshotProgress.snapshot} />
          )}
          {snapshotPollFailed && (
            <div className="text-xs text-danger">Не удалось обновить статус snapshot; следующая попытка через 5 секунд.</div>
          )}
        </form>
      )}
    </section>
  );
}

/**
 * Whitelist of Telegram group chats the assistant may answer in. Everything
 * else — other groups and any private message — stays silently ignored.
 */
function ChatList({
  chats,
  inputClass,
  onMessage,
  onChanged,
}: {
  chats: AssistantChat[];
  inputClass: string;
  onMessage: (message: { ok: boolean; text: string } | null) => void;
  onChanged: () => void;
}) {
  const [chatId, setChatId] = useState('');
  const [label, setLabel] = useState('');

  const onError = (error: Error) => onMessage({ ok: false, text: error.message });
  const add = useMutation({
    mutationFn: () => addAssistantChat({ chat_id: chatId.trim(), label: label.trim() }),
    onSuccess: () => {
      onMessage({ ok: true, text: 'Чат подключён.' });
      setChatId('');
      setLabel('');
      onChanged();
    },
    onError,
  });
  const update = useMutation({
    mutationFn: (args: { chatId: string; enabled: boolean }) =>
      updateAssistantChat(args.chatId, { enabled: args.enabled }),
    onSuccess: () => {
      onMessage(null);
      onChanged();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => removeAssistantChat(id),
    onSuccess: () => {
      onMessage({ ok: true, text: 'Чат отключён, его история диалога удалена.' });
      onChanged();
    },
    onError,
  });

  const submitChat = () => {
    if (!chatId.trim()) return;
    onMessage(null);
    add.mutate();
  };

  return (
    <div className="border border-border rounded-md p-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h3 className="text-xs font-semibold text-content-muted">Подключённые чаты</h3>
        <p className="text-xs text-content-subtle">
          Все остальные чаты молча игнорируются. У каждого чата своя история диалога.
        </p>
      </div>

      {chats.length === 0 ? (
        <p className="mt-2 text-xs text-content-subtle">Пока ни одного чата — ассистент не отвечает нигде.</p>
      ) : (
        <ul className="mt-2 divide-y divide-border">
          {chats.map((chat) => (
            <li key={chat.chat_id} className="flex items-center gap-3 py-2 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={chat.enabled}
                  disabled={update.isPending}
                  onChange={(event) => update.mutate({ chatId: chat.chat_id, enabled: event.target.checked })}
                  className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
                />
                <span className="text-sm text-content">{chat.label || 'Без названия'}</span>
              </label>
              <span className="font-mono text-xs text-content-subtle">{chat.chat_id}</span>
              {!chat.enabled && <span className="text-xs text-content-subtle">выключен</span>}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="ml-auto"
                loading={remove.isPending && remove.variables === chat.chat_id}
                onClick={() => {
                  if (!confirm(`Отключить чат ${chat.label || chat.chat_id}? История диалога будет удалена.`)) return;
                  onMessage(null);
                  remove.mutate(chat.chat_id);
                }}
              >
                Отключить
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-[minmax(0,200px)_minmax(0,1fr)_auto] gap-2">
        <input
          value={chatId}
          onChange={(event) => setChatId(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            submitChat();
          }}
          className={`${inputClass} font-mono`}
          placeholder="-1001234567890"
        />
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            submitChat();
          }}
          className={inputClass}
          placeholder="Название (необязательно — подставится из Telegram)"
        />
        <Button
          type="button"
          variant="secondary"
          loading={add.isPending}
          disabled={!chatId.trim()}
          onClick={submitChat}
        >
          Подключить чат
        </Button>
      </div>
      <p className="mt-1 text-xs text-content-subtle">
        Бот должен быть добавлен в группу или супергруппу до подключения. После добавления чата нажмите «Подключить webhook», если он ещё не настроен.
      </p>
    </div>
  );
}

function dmUserTitle(user: AssistantDmUser): string {
  return user.name || (user.username ? `@${user.username}` : `user ${user.user_id}`);
}

function shortDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/**
 * Who may talk to the assistant in private. The first DM from anyone files a
 * request; until a super admin approves it here the bot only says "wait".
 */
function DmUserList({
  users,
  onMessage,
  onChanged,
}: {
  users: AssistantDmUser[];
  onMessage: (message: { ok: boolean; text: string } | null) => void;
  onChanged: () => void;
}) {
  const onError = (error: Error) => onMessage({ ok: false, text: error.message });
  const decide = useMutation({
    mutationFn: (args: { userId: string; status: 'approved' | 'rejected' }) =>
      decideAssistantDmUser(args.userId, args.status),
    onSuccess: (_data, args) => {
      onMessage({ ok: true, text: args.status === 'approved' ? 'Доступ открыт, человеку отправлено уведомление.' : 'Запрос отклонён.' });
      onChanged();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (userId: string) => removeAssistantDmUser(userId),
    onSuccess: () => {
      onMessage({ ok: true, text: 'Пользователь удалён, его история диалога стёрта.' });
      onChanged();
    },
    onError,
  });

  const pending = users.filter((u) => u.status === 'pending');
  const approved = users.filter((u) => u.status === 'approved');
  const rejected = users.filter((u) => u.status === 'rejected');
  const busy = (userId: string) =>
    (decide.isPending && decide.variables?.userId === userId) || (remove.isPending && remove.variables === userId);

  const row = (user: AssistantDmUser, actions: React.ReactNode) => (
    <li key={user.user_id} className="flex items-center gap-3 py-2 flex-wrap">
      <span className="text-sm text-content">{dmUserTitle(user)}</span>
      {user.username && user.name && <span className="text-xs text-content-subtle">@{user.username}</span>}
      <span className="font-mono text-xs text-content-subtle">{user.user_id}</span>
      <span className="text-xs text-content-subtle">
        {user.status === 'pending'
          ? `запрос ${shortDate(user.requested_at)}`
          : `${user.status === 'approved' ? 'одобрил' : 'отклонил'} ${user.decided_by ?? '—'} ${shortDate(user.decided_at)}`}
      </span>
      <span className="ml-auto flex items-center gap-2">{actions}</span>
    </li>
  );

  return (
    <div className="border border-border rounded-md p-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h3 className="text-xs font-semibold text-content-muted">
          Личные сообщения
          {pending.length > 0 && (
            <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-semibold">
              {pending.length} {pending.length === 1 ? 'запрос' : 'запросов'}
            </span>
          )}
        </h3>
        <p className="text-xs text-content-subtle">
          Первое сообщение боту в личку создаёт запрос. Пока запрос не одобрен, бот отвечает только «подождите».
        </p>
      </div>

      {users.length === 0 && (
        <p className="mt-2 text-xs text-content-subtle">Пока никто не писал боту в личку.</p>
      )}

      {pending.length > 0 && (
        <>
          <p className="mt-2 text-[11px] uppercase tracking-wide text-content-subtle">Ждут одобрения</p>
          <ul className="divide-y divide-border">
            {pending.map((user) => row(user, (
              <>
                <Button
                  type="button"
                  size="sm"
                  loading={busy(user.user_id)}
                  onClick={() => { onMessage(null); decide.mutate({ userId: user.user_id, status: 'approved' }); }}
                >
                  Одобрить
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={busy(user.user_id)}
                  onClick={() => { onMessage(null); decide.mutate({ userId: user.user_id, status: 'rejected' }); }}
                >
                  Отклонить
                </Button>
              </>
            )))}
          </ul>
        </>
      )}

      {approved.length > 0 && (
        <>
          <p className="mt-3 text-[11px] uppercase tracking-wide text-content-subtle">Могут общаться</p>
          <ul className="divide-y divide-border">
            {approved.map((user) => row(user, (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={busy(user.user_id)}
                onClick={() => {
                  if (!confirm(`Закрыть доступ для ${dmUserTitle(user)}?`)) return;
                  onMessage(null);
                  decide.mutate({ userId: user.user_id, status: 'rejected' });
                }}
              >
                Отозвать
              </Button>
            )))}
          </ul>
        </>
      )}

      {rejected.length > 0 && (
        <>
          <p className="mt-3 text-[11px] uppercase tracking-wide text-content-subtle">Отклонены</p>
          <ul className="divide-y divide-border">
            {rejected.map((user) => row(user, (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={busy(user.user_id)}
                  onClick={() => { onMessage(null); decide.mutate({ userId: user.user_id, status: 'approved' }); }}
                >
                  Одобрить
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={busy(user.user_id)}
                  onClick={() => {
                    if (!confirm(`Удалить ${dmUserTitle(user)} из списка? Написав снова, человек создаст новый запрос.`)) return;
                    onMessage(null);
                    remove.mutate(user.user_id);
                  }}
                >
                  Удалить
                </Button>
              </>
            )))}
          </ul>
        </>
      )}
    </div>
  );
}

const STAGE_LABELS: Record<string, string> = {
  queued: 'Ожидает cron',
  exporting: 'D1 export',
  uploading: 'Чтение D1 и загрузка в R2',
  backup_ready: 'Backup загружен',
  snapshot_waiting: 'Backup готов, копия для ассистента ждёт свой cron-запуск',
  snapshot_export: 'Экспорт копии для ассистента',
  snapshot_queued: 'Snapshot поставлен на импорт',
  completed: 'Backup завершён',
  error: 'Ошибка',
};

const SNAPSHOT_PHASE_LABELS: Record<string, string> = {
  empty: 'Snapshot ещё не создан',
  queued: 'Ожидает запуска Workflow',
  uploading: 'Workflow заливает первую часть в D1',
  ingesting: 'D1 импортирует части снапшота',
  ready: 'Snapshot готов',
  error: 'Ошибка snapshot',
};

function SnapshotProgress({
  status,
  snapshot,
}: {
  status: import('@/api/assistant').ManualBackupStatus;
  snapshot: import('@/api/assistant').AssistantSnapshotProgress['snapshot'];
}) {
  const bad = status.state === 'error' || status.state === 'stalled' || snapshot.status === 'error';
  const active = status.state === 'queued' || status.state === 'running';
  return (
    <div className={`rounded-md border px-3 py-2 text-xs ${bad ? 'border-danger/30 bg-danger-soft text-danger' : 'border-border bg-surface-subtle text-content-muted'}`}>
      <div className="font-medium">
        {active ? 'Выполняется' : bad ? 'Запуск не завершён' : 'Завершено'}: {STAGE_LABELS[status.stage] ?? status.stage}
        {status.state === 'stalled' ? ' · можно запустить повторно' : ''}
      </div>
      <div className="mt-1">Backup обновлён: {new Date(status.updatedAt).toLocaleString()}</div>
      <div className="mt-1">
        {SNAPSHOT_PHASE_LABELS[snapshot.phase] ?? `Snapshot: ${snapshot.status}`}
        {snapshot.parts_total
          ? ` · часть ${snapshot.parts_done ?? 0} из ${snapshot.parts_total}`
          : ''}
        {snapshot.workflow_status ? ` · Workflow: ${snapshot.workflow_status}` : ''}
      </div>
      {snapshot.updated_at && (
        <div className="mt-1">Snapshot обновлён: {new Date(snapshot.updated_at).toLocaleString()}</div>
      )}
      {snapshot.error && <div className="mt-1 font-mono break-all">{snapshot.error}</div>}
      {status.stage === 'uploading' && status.sourceBytes !== undefined && (
        <div className="mt-1">Прочитано из D1: {(status.sourceBytes / (1024 * 1024)).toFixed(1)} MB</div>
      )}
      {status.stage === 'uploading' && status.uploadedBytes !== undefined && (
        <div className="mt-1">Загружено в R2: {(status.uploadedBytes / (1024 * 1024)).toFixed(1)} MB</div>
      )}
      {status.error && <div className="mt-1 font-mono break-all">{status.error}</div>}
      <details className="mt-2">
        <summary className="cursor-pointer">Журнал стадий</summary>
        <ol className="mt-1 space-y-1 font-mono">
          {status.events.map((event, index) => (
            <li key={`${event.at}-${index}`}>{new Date(event.at).toLocaleTimeString()} — {STAGE_LABELS[event.stage] ?? event.stage}{event.message ? ` — ${event.message}` : ''}</li>
          ))}
        </ol>
      </details>
    </div>
  );
}

function SecretField(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  configured: boolean;
  preview?: string;
  onRemove?: () => void;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-content-muted mb-1">{props.label}</label>
      <input
        type="password"
        autoComplete="new-password"
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
        placeholder={props.configured ? `задан: ${props.preview || '••••'} · введите новый для замены` : 'не задан'}
      />
      {props.configured && props.onRemove && (
        <button type="button" onClick={props.onRemove} className="mt-1 text-xs text-danger hover:underline">
          Удалить
        </button>
      )}
    </div>
  );
}
